import { useEffect, useState } from 'react';

/**
 * Tracks browser online/offline status.
 *
 * Returns:
 *  - `isOnline`: huidige status (true bij start als navigator.onLine === true)
 *  - `wasOffline`: true gedurende 3s nadat de gebruiker terugkeert van offline → online
 *    (handig om kort een "weer verbonden" melding te tonen)
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  });
  const [wasOffline, setWasOffline] = useState(false);
  useEffect(() => {
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const handleOnline = () => {
      // Alleen als we daadwerkelijk eerder offline waren een "weer online" pulse tonen
      setIsOnline(prev => {
        if (!prev) {
          setWasOffline(true);
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => setWasOffline(false), 3000);
        }
        return true;
      });
    };
    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(false);
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);
  return {
    isOnline,
    wasOffline
  };
}